import React, { useState, useEffect } from 'react';
import { IoClose, IoTrashOutline } from 'react-icons/io5';
import axiosInstance from '../../api/axiosInstance';

interface GalleryImage {
  id: number;
  messageId: number;
  imageDataUrl: string;
  createdAtUtc: string;
  fromUserId?: number;
}

interface GalleryProps {
  userA: string;
  userB: string;
  currentUserId: string;
  onDeleteMessage: (messageId: number) => void;
  onClose: () => void;
}

const GalleryNew: React.FC<GalleryProps> = ({ userA, userB, currentUserId, onDeleteMessage, onClose }) => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<GalleryImage | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        // Cache-busting timestamp to force fresh data
        const cacheBust = new Date().getTime();
        const { data } = await axiosInstance.get('/api/media/gallery', {
          params: { userA, userB, _: cacheBust }
        });
        console.log('📸 Gallery loaded:', data.length, 'images');
        // Filter to only show JPEG images with valid data URLs
        const jpegImages = (Array.isArray(data) ? data : []).filter((img: GalleryImage) => 
          img.imageDataUrl && 
          img.imageDataUrl.startsWith('data:image/jpeg') &&
          img.imageDataUrl.length > 0
        );
        console.log('📸 Filtered JPEG images:', jpegImages.length);
        setImages(jpegImages);
        setError(null);
      } catch (err) {
        console.error('❌ Gallery error:', err);
        setError('Failed to load gallery');
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [userA, userB]);

  const currentUserIdNum = parseInt(currentUserId);
  // If API doesn't provide fromUserId (e.g. older prod backend), allow showing the delete action;
  // backend still enforces authorization, and we’ll refresh after attempting.
  const canDeleteSelected =
    !!selected && (selected.fromUserId == null || selected.fromUserId === currentUserIdNum);

  const handleDeleteSelected = async () => {
    if (!selected) return;
    if (!canDeleteSelected) return;
    if (deleting) return;

    const ok = window.confirm('Delete this image?');
    if (!ok) return;

    setDeleting(true);
    try {
      // Delete the underlying message (server will soft-delete it)
      onDeleteMessage(selected.messageId);

      // Optimistic UI update
      setImages(prev => prev.filter(img => img.messageId !== selected.messageId));
      setSelected(null);
      // Revalidate from server shortly after to reflect actual backend state
      setTimeout(async () => {
        try {
          const cacheBust = new Date().getTime();
          const { data } = await axiosInstance.get('/api/media/gallery', {
            params: { userA, userB, _: cacheBust }
          });
          const jpegImages = (Array.isArray(data) ? data : []).filter((img: GalleryImage) =>
            img.imageDataUrl &&
            img.imageDataUrl.startsWith('data:image/jpeg') &&
            img.imageDataUrl.length > 0
          );
          setImages(jpegImages);
        } catch (e) {
          // ignore revalidate errors
        }
      }, 800);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col items-center justify-center gap-4">
        <p className="text-red-400 text-lg">{error}</p>
        <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
          Close
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col">
        <div className="flex justify-between items-center p-4 bg-[#1C2731]">
          <div>
            <h2 className="text-xl font-semibold text-white">Gallery</h2>
            <p className="text-sm text-gray-400">{images.length} shared images</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-white"
          >
            <IoClose size={28} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {images.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-gray-400 text-lg">No images yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-w-4xl mx-auto">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="aspect-square cursor-pointer overflow-hidden rounded-lg bg-gray-800"
                  onClick={() => setSelected(image)}
                >
                  <img
                    src={image.imageDataUrl}
                    alt={`Gallery item ${image.id}`}
                    className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                    onError={(e) => {
                      console.error('Image failed to load:', image.id);
                      e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EError%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-[60] flex items-center justify-center p-4">
          <button
            onClick={() => {
              if (!canDeleteSelected) {
                alert('Only the sender can delete this image.');
                return;
              }
              handleDeleteSelected();
            }}
            disabled={deleting || !canDeleteSelected}
            className={`absolute top-4 left-4 p-2 rounded-full transition-colors text-white z-10 ${
              deleting
                ? 'bg-red-900 cursor-not-allowed opacity-70'
                : canDeleteSelected
                ? 'bg-red-600 hover:bg-red-500'
                : 'bg-gray-700 cursor-not-allowed opacity-60'
            }`}
            title={canDeleteSelected ? 'Delete' : 'You can only delete images you sent'}
          >
            <IoTrashOutline size={28} />
          </button>
          <button
            onClick={() => setSelected(null)}
            className="absolute top-4 right-4 p-2 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors text-white z-10"
          >
            <IoClose size={32} />
          </button>
          <img
            src={selected.imageDataUrl}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={() => setSelected(null)}
          />
        </div>
      )}
    </>
  );
};

export default GalleryNew;
